import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import TeacherLogin from '../components/LoginModules/TeacherLogin';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(location.pathname !== '/');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showTeacherLogin, setShowTeacherLogin] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        setIsScrolled(window.scrollY > 50);
      }
    };
    if (location.pathname !== '/') {
      setIsScrolled(true); // force dark header if not homepage
    } else {
      setIsScrolled(window.scrollY > 50);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const menuItems = [
    { name: 'Home', href: '#home', isHome: true },
    { name: 'About', href: '#purpose' },
    { name: 'DOK Levels', href: '#dok-levels' },
    { name: 'Features', href: '#features' },
    { name: 'Pricing', href: '/pricing', isRoute: true },
    { name: 'FAQ', href: '/FAQ', isRoute: true },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Student Access', href: '/student-access', isRoute: true }
  ];

  const scrollToHash = (hash) => {
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavigation = (item) => {
    setIsMobileMenuOpen(false);

    if (item.isRoute) {
      navigate(item.href);
    } else if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToHash(item.href), 300);
    } else {
      scrollToHash(item.href);
    }
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            className="flex items-center gap-2 text-2xl font-bold transition-colors cursor-pointer"
            style={{ color: isScrolled ? '#56643F' : '#DCD9CA' }}
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/')}
          >
            <img
              src={require(`../assets/logo/${isScrolled ? 'gradely-logo.png' : 'gradely-logo-1.png'}`)}
              alt="Gradely Logo"
              className="h-20 w-auto object-contain"
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
          </motion.div>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavigation(item)}
                className={`transition-colors ${isScrolled ? 'text-[#56643F]' : 'text-[#DCD9CA]'
                  }`}
                whileHover={{ y: -2 }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.name}
              </motion.button>
            ))}
            <Button
              className="bg-[#DCD9CA] hover:bg-[#56643F] text-black hover:text-white transition-colors"
              onClick={() => setShowTeacherLogin(true)}
            >
              Login To Dashboard
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button
            className={`md:hidden ${isScrolled ? 'text-[#56643F]' : 'text-[#DCD9CA]'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.nav
            className="md:hidden mt-4 py-4 bg-white rounded-lg shadow-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {menuItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                className="block w-full text-left px-4 py-2 text-foreground hover:text-[#DCD9CA] hover:bg-accent transition-colors"
              >
                {item.name}
              </button>
            ))}
            <div className="px-4 py-2">
              <Button
                className="w-full bg-[#DCD9CA] hover:bg-[#56643F] text-black hover:text-white transition-colors"
                onClick={() => setShowTeacherLogin(true)}
              >
                Login To Dashboard
              </Button>
            </div>
          </motion.nav>
        )}
      </div>

      {/* Modal */}
      {showTeacherLogin && (
        <TeacherLogin
          onLogin={() => navigate('/teacher-dashboard')}
          onClose={() => setShowTeacherLogin(false)}
        />
      )}
    </motion.header>
  );
};

export default Header;
